/**
 * @flow
 */
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { localPoint } from '@visx/event';
import { LegendItem, LegendLabel, LegendOrdinal } from '@visx/legend';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import findLast from 'lodash/findLast';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Localize } from 'react-redux-i18n';
import compactNumber from 'utils/compactNumber';
import { I18n } from 'utils/i18n';

type Props = {
  title: string,
  description: string,
  data?: Array<Object>,
  totals?: Object,
  chart: Object,
  keys?: Array<string>,
  color?: Function,
  xScale?: Object,
  x?: Function,
  tickFormat?: Function,
  controls?: Object,
  sx?: Object,
  yTickFormats?: {
    [string]: Function,
  },
};

const ChartSection = ({
  title,
  description,
  keys = [],
  color = () => '#000',
  data = [],
  chart,
  xScale = null,
  x = null,
  tickFormat = value => value.toString(),
  totals = {},
  controls = null,
  sx = null,
  yTickFormats = {},
}: Props) => {
  const theme = useTheme();
  const locale = useSelector(state => state.i18n.locale);
  const numberFormat = new Intl.NumberFormat(locale, { minimumFractionDigits: 0 });
  const {
    tooltipData: ttd,
    tooltipLeft: ttl,
    tooltipTop: ttt,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [tooltipLeft, setTooltipLeft] = useState(ttl);
  const [tooltipTop, setTooltipTop] = useState(ttt);
  const [tooltipData, setTooltipData] = useState(ttt);

  const tooltipStyle = {
    position: 'absolute',
    padding: '15px',
    borderRadius: '5px',
    border: `1px solid ${theme.palette.secondary.light}`,
    minWidth: 210,
    background: alpha(theme.palette.background.paper, 0.9),
    boxShadow: '0 1px 22px 0 rgba(2,2,3,0.25)',
    backdropFilter: 'blur(2px)',
  };

  useEffect(() => {
    if (tooltipOpen) {
      setTooltipLeft(ttl);
      setTooltipTop(ttt);
      setTooltipData(ttd);
    }
  }, [ttt, ttl, ttd, tooltipOpen]);
  const handleMouseOver = event => {
    const domain = xScale?.domain?.();
    const paddingOuter = xScale && domain ? xScale(domain[0]) : 0;
    const coords = localPoint(event.target?.ownerSVGElement, event);
    if (!coords) return;
    let td;
    let ttipLeft;
    let ttipTop;
    if (!data?.length) {
      ttipLeft = event.nativeEvent.offsetX;
      ttipTop = event.nativeEvent.offsetY;
      td = data;
    } else if (xScale && typeof xScale.step === 'function') {
      const eachBand = xScale.step();
      const index = Math.round((coords.x - paddingOuter) / eachBand);
      const val = domain && domain[Math.max(0, Math.min(index, domain.length - 1))];
      td = data?.find(d => x && val !== undefined && x(d) === val);
    } else {
      const val = xScale && xScale.invert && coords && xScale.invert(coords.x);
      td =
        val !== undefined ? findLast(data, d => x && x(d) !== undefined && x(d) <= val) : undefined;
    }
    if (td) {
      ttipLeft =
        ttipLeft ||
        (xScale && x && typeof x === 'function' && typeof xScale === 'function'
          ? xScale(x(td)) + 25
          : coords.x + 25);
      ttipTop = ttipTop || coords.y;
      showTooltip({
        tooltipLeft: ttipLeft,
        tooltipTop: ttipTop,
        tooltipData: td,
      });
    }
  };

  const handleMouseLeave = () => {
    setTimeout(() => {
      hideTooltip();
    }, 100);
  };

  return (
    <Box
      component="section"
      sx={[
        {
          display: 'flex',
          justifyContent: 'space-between',
          m: '0 40px 0 110px',
          flexWrap: 'wrap',
          '@media (max-width: 800px)': {
            ml: '20px',
          },
        },
        ...(sx ? [sx] : []),
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mr: '40px',
          flex: 1,
          mt: '60px',
        }}
      >
        <Typography component="h6" variant="h6">
          {title}
        </Typography>
        <Typography component="div" variant="subtitle2">
          {description}
        </Typography>
        {!!color && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: '22px' }}>
            <LegendOrdinal
              scale={color}
              itemDirection="row-reverse"
              direction="column"
              labelMargin="0 15px 0 0"
            >
              {labels => {
                return labels.map((label, idx) => {
                  const size = 10;
                  const value = compactNumber(totals?.[label.text]);
                  if (Number.isNaN(value.n)) return null;
                  return (
                    <LegendItem
                      alignItems="baseline"
                      key={`legend-${label.value}`}
                      style={{ alignItems: 'flex-start', marginTop: '20px' }}
                    >
                      <div
                        style={{
                          backgroundColor: label.value,
                          width: size,
                          height: size,
                          borderRadius: '50%',
                          margin: '2px 0',
                        }}
                      />
                      <LegendLabel
                        style={{
                          margin: '0 60px 0 10px',
                          fontSize: 14,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography component="div">
                          {I18n.t(`AnalyticsScene.labels.${label.text}`)}
                        </Typography>
                        <Typography
                          component="div"
                          sx={{ fontSize: 18, fontWeight: 'bold' }}
                          title={value.n}
                        >
                          {yTickFormats && keys && yTickFormats[keys[idx]] ? (
                            yTickFormats[keys[idx]](+totals?.[label.text])
                          ) : (
                            <>
                              <Localize value={value.n} options={{ maximumFractionDigits: 2 }} />
                              {value.suffix}
                            </>
                          )}
                        </Typography>
                      </LegendLabel>
                    </LegendItem>
                  );
                });
              }}
            </LegendOrdinal>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          bgcolor: 'secondary.ultraLight',
          borderRadius: '20px',
          p: '20px 30px',
          position: 'relative',
          mt: '30px',
          mb: '30px',
        }}
      >
        {controls}
        {chart(handleMouseOver, handleMouseLeave, tooltipData, tooltipOpen && tooltipLeft)}
        {!!tooltipLeft && (
          <Box
            sx={{
              opacity: tooltipOpen ? 1 : 0,
              transition: 'all .25s ease-in-out',
              pointerEvents: 'none',
            }}
          >
            <TooltipWithBounds
              // set this to random so it correctly updates with parent bounds
              key={Math.random()}
              top={tooltipTop}
              offsetLeft={25}
              left={tooltipLeft}
              style={tooltipStyle}
            >
              <Box sx={{ fontSize: 14, color: 'secondary.main', mb: '15px' }}>
                {tickFormat && tooltipData && tickFormat(x?.(tooltipData))}
              </Box>
              {keys
                ?.filter(key => typeof tooltipData[key] !== 'undefined')
                .map(key => (
                  <Box
                    key={key}
                    sx={{
                      fontSize: 14,
                      display: 'flex',
                      color: 'text.primary',
                      alignItems: 'center',
                      mb: '10px',
                      '& > div + div': {
                        ml: '10px',
                      },
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: color?.(key),
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                      }}
                    />
                    <div>{I18n.t(`AnalyticsScene.labels.${key}`)}</div>
                    <Box
                      sx={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        flex: 1,
                        display: 'flex',
                      }}
                    >
                      {((yTickFormats && yTickFormats[key]) || numberFormat.format)(
                        tooltipData?.[key],
                      )}
                    </Box>
                  </Box>
                ))}
            </TooltipWithBounds>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChartSection;
